/**
 * Mock 数据生成工具 - 基础数据生成
 *
 * 职责范围：
 * - 生成基础的反馈数据（FeedbackData）
 * - 生成仪表板数据（DashboardData）
 * - 用于客诉系统、反馈列表等基础场景
 *
 * 注意：如需分析相关的聚合数据（时序、散点、词云等），请使用 utils/mockData.ts
 */
import type { FeedbackData, Sentiment } from '../types/feedback';
import type { DashboardData, MetricStatus } from '../types/dashboard';

// 产品数据
const products = [
  { id: 'prod-001', name: '智能音箱 X1', category: '智能家居', brand: '品牌A' },
  { id: 'prod-002', name: '智能手表 S2', category: '可穿戴设备', brand: '品牌A' },
  { id: 'prod-003', name: '扫地机器人 R3', category: '智能家居', brand: '品牌B' },
  { id: 'prod-004', name: '无线耳机 E4', category: '消费电子', brand: '品牌B' },
  { id: 'prod-005', name: '智能门锁 L5', category: '智能家居', brand: '品牌A' }
];

// 渠道数据
const channels = [
  { id: 'channel-001', name: '客诉系统' },
  { id: 'channel-002', name: '亚马逊' },
  { id: 'channel-003', name: '社交媒体' },
  { id: 'channel-004', name: '调研问卷' },
  { id: 'channel-005', name: '工单系统' }
];

// 体验标签
const experienceTags = [
  '易用性', '性能', '外观', '功能完整性', '客服体验',
  '配送体验', '价格', '质量', '兼容性', '安全性'
];

// 常见关键词
const keywords = [
  '好用', '卡顿', '漂亮', '声音', '连接', '电池', '质量',
  '客服', '快递', '价格', '功能', '简单', '复杂', '满意'
];

// 生成随机反馈内容
function generateFeedbackContent(sentiment: Sentiment): string {
  const positiveTemplates = [
    '产品非常好用,使用体验很棒',
    '质量很不错,物有所值',
    '功能很齐全,满足需求',
    '外观设计很漂亮,很满意',
    '客服态度很好,问题解决及时'
  ];

  const neutralTemplates = [
    '产品还可以,符合基本需求',
    '整体还行,有些小问题',
    '使用正常,没有特别惊喜',
    '价格合理,性价比一般',
    '功能够用,但还有提升空间'
  ];

  const negativeTemplates = [
    '产品质量有问题,经常出故障',
    '使用体验很差,操作复杂',
    '功能不完善,很多需求无法满足',
    '客服响应慢,问题迟迟不解决',
    '价格太贵,性价比不高'
  ];

  let templates;
  switch (sentiment) {
    case 'positive':
      templates = positiveTemplates;
      break;
    case 'negative':
      templates = negativeTemplates;
      break;
    default:
      templates = neutralTemplates;
  }

  return templates[Math.floor(Math.random() * templates.length)];
}

// 生成随机满意度评分
function generateSatisfactionScore(sentiment: Sentiment): number {
  switch (sentiment) {
    case 'positive':
      return Math.floor(Math.random() * 3) + 8; // 8-10
    case 'negative':
      return Math.floor(Math.random() * 4) + 1; // 1-4
    default:
      return Math.floor(Math.random() * 3) + 5; // 5-7
  }
}

// 生成随机日期
function generateRandomDate(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * daysAgo));
  return date.toISOString();
}

// 生成单条反馈数据
export function generateFeedbackData(index: number): FeedbackData {
  const sentiments: Sentiment[] = ['positive', 'neutral', 'negative'];
  const sentiment = sentiments[Math.floor(Math.random() * sentiments.length)];
  const product = products[Math.floor(Math.random() * products.length)];
  const channel = channels[Math.floor(Math.random() * channels.length)];

  return {
    id: `feedback-${String(index).padStart(6, '0')}`,
    content: generateFeedbackContent(sentiment),
    productId: product.id,
    productName: product.name,
    productCategory: product.category,
    productBrand: product.brand,
    channel: channel.id,
    channelName: channel.name,
    satisfactionScore: generateSatisfactionScore(sentiment),
    sentiment,
    experienceTags: experienceTags
      .sort(() => 0.5 - Math.random())
      .slice(0, Math.floor(Math.random() * 3) + 1),
    keywords: keywords
      .sort(() => 0.5 - Math.random())
      .slice(0, Math.floor(Math.random() * 3) + 1),
    createdAt: generateRandomDate(30),
    updatedAt: generateRandomDate(30)
  };
}

// 生成批量反馈数据
export function generateFeedbackList(count: number): FeedbackData[] {
  return Array.from({ length: count }, (_, i) => generateFeedbackData(i + 1));
}

// 生成仪表板 Mock 数据
export function generateDashboardData(): DashboardData {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // 生成情感趋势数据（最近7天）
  const sentimentTrend = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));

    const total = Math.floor(Math.random() * 100) + 150;
    const positive = Math.floor(total * (0.5 + Math.random() * 0.2));
    const negative = Math.floor(total * (0.1 + Math.random() * 0.15));
    const neutral = total - positive - negative;

    return {
      date: date.toISOString().split('T')[0],
      positive,
      neutral,
      negative,
      total
    };
  });

  // 计算总反馈量和趋势
  const todayTotal = Math.floor(Math.random() * 100) + 200;
  const yesterdayTotal = Math.floor(Math.random() * 100) + 180;
  const feedbackTrend = ((todayTotal - yesterdayTotal) / yesterdayTotal * 100);

  // 计算平均满意度
  const avgSatisfaction = 7.2 + Math.random() * 1.5;
  const satisfactionChange = (Math.random() - 0.5) * 20;

  // 判断异常状态
  const satisfactionStatus: MetricStatus =
    satisfactionChange < -10 ? 'error' :
    satisfactionChange < -5 ? 'warning' : 'normal';

  const feedbackStatus: MetricStatus =
    feedbackTrend > 50 ? 'warning' : 'normal';

  return {
    overview: {
      totalFeedback: {
        title: '今日反馈总量',
        value: todayTotal,
        unit: '条',
        trend: {
          value: Math.abs(feedbackTrend),
          direction: feedbackTrend >= 0 ? 'up' : 'down',
          period: 'vs 昨日'
        },
        status: feedbackStatus
      },
      avgSatisfaction: {
        title: '平均满意度',
        value: avgSatisfaction.toFixed(1),
        unit: '分',
        trend: {
          value: Math.abs(satisfactionChange),
          direction: satisfactionChange >= 0 ? 'up' : 'down',
          period: 'vs 上周'
        },
        status: satisfactionStatus
      },
      positiveRate: {
        title: '正面情感占比',
        value: (Math.random() * 20 + 55).toFixed(1),
        unit: '%',
        trend: {
          value: Math.random() * 5,
          direction: Math.random() > 0.5 ? 'up' : 'down',
          period: 'vs 上周'
        },
        status: 'normal'
      },
      responseRate: {
        title: '响应及时率',
        value: (Math.random() * 10 + 85).toFixed(1),
        unit: '%',
        trend: {
          value: Math.random() * 3,
          direction: 'up',
          period: 'vs 上周'
        },
        status: 'normal'
      }
    },
    topIssues: [
      { id: '1', keyword: '卡顿', count: 45, sentiment: 'negative', change: 12 },
      { id: '2', keyword: '连接问题', count: 38, sentiment: 'negative', change: 5 },
      { id: '3', keyword: '电池续航', count: 32, sentiment: 'negative', change: -3 },
      { id: '4', keyword: '好用', count: 28, sentiment: 'positive', change: 8 },
      { id: '5', keyword: '客服态度', count: 25, sentiment: 'negative', change: 15 }
    ],
    channelDistribution: [
      { channel: 'channel-001', channelName: '客诉系统', count: 450, percentage: 30 },
      { channel: 'channel-002', channelName: '亚马逊', count: 380, percentage: 25 },
      { channel: 'channel-003', channelName: '社交媒体', count: 330, percentage: 22 },
      { channel: 'channel-004', channelName: '调研问卷', count: 230, percentage: 15 },
      { channel: 'channel-005', channelName: '工单系统', count: 120, percentage: 8 }
    ],
    categoryDistribution: [
      { category: '智能家居', count: 580, percentage: 38.5 },
      { category: '可穿戴设备', count: 420, percentage: 28 },
      { category: '消费电子', count: 350, percentage: 23.2 },
      { category: '智能安防', count: 156, percentage: 10.3 }
    ],
    sentimentTrend,
    lastUpdateTime: new Date().toISOString()
  };
}
